import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CircularProgress } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { FlexBox } from '../../../global/FlexBox';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { DiamDrawer, DiamDrawerCancelButton } from '../DiamDrawer/DiamDrawer';
import { CREATE_TEAM_DRAWER_ID, NONE_OPTION_VALUE } from '../../constants';
import { useMinimizedDrawers } from '../../context/MinimizedDrawersContext';
import { buildCreateTeamValidationSchema } from './validationSchema';
import { TeamFormFields } from './TeamFormFields';
import { useCreateTeamApplicationOptions } from './useCreateTeamApplicationOptions';
import { useCreateTeamModifiers } from './useCreateTeamModifiers';
import { useCreateTeam } from './useCreateTeam';
import { useParentTeamTree } from './useParentTeamTree';
import { findTreeNodeLabel } from './treeUtils';
import { CreateTeamFormData } from './types';
import {
  buildCreateTeamSubmitPayload,
  deserializeCreateTeamFormData,
  buildDefaultOptionalModifiers,
  getDefaultCreateTeamFormData,
  isCreateTeamDrawerRestore,
  serializeCreateTeamFormData,
} from './formUtils';

interface CreateTeamDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

export function CreateTeamDrawer({
  isOpen,
  onClose,
  onOpen,
}: CreateTeamDrawerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { minimizeDrawer, lastRestoredItem, clearLastRestoredItem } =
    useMinimizedDrawers();

  const {
    accountOptions,
    locationTree,
    modifierFieldsConfig,
    isLoading: areModifiersLoading,
    isError: areModifiersError,
    error: modifiersError,
  } = useCreateTeamModifiers(isOpen);
  const {
    applicationOptions,
    isLoading: areApplicationsLoading,
    isError: areApplicationsError,
    error: applicationsError,
  } = useCreateTeamApplicationOptions(isOpen);
  const {
    parentTeamTree,
    isLoading: isParentTeamLoading,
    isError: isParentTeamError,
    error: parentTeamError,
  } = useParentTeamTree(isOpen);

  const { mutate: createTeam, isPending } = useCreateTeam();

  const defaultValues = useMemo(
    () => getDefaultCreateTeamFormData(modifierFieldsConfig),
    [modifierFieldsConfig],
  );

  const validationSchema = useMemo(
    () => buildCreateTeamValidationSchema(modifierFieldsConfig),
    [modifierFieldsConfig],
  );

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    clearErrors,
    watch,
    setValue,
  } = useForm<CreateTeamFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const selectedApplications = watch('applications');

  useEffect(() => {
    if (isParentTeamLoading) {
      return;
    }

    const currentParentTeamId = getValues('parentTeamId');
    if (
      currentParentTeamId &&
      !findTreeNodeLabel(parentTeamTree, currentParentTeamId)
    ) {
      setValue('parentTeamId', '');
    }
  }, [getValues, isParentTeamLoading, parentTeamTree, setValue]);

  useEffect(() => {
    if (!isOpen || !areApplicationsError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(applicationsError, 'Failed to load applications'),
      { variant: 'error' },
    );
  }, [applicationsError, areApplicationsError, enqueueSnackbar, isOpen]);

  useEffect(() => {
    if (!isOpen || !isParentTeamError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(parentTeamError, 'Failed to load parent teams'),
      { variant: 'error' },
    );
  }, [enqueueSnackbar, isOpen, isParentTeamError, parentTeamError]);

  useEffect(() => {
    if (!isOpen || !areModifiersError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(modifiersError, 'Failed to load account and location'),
      { variant: 'error' },
    );
  }, [areModifiersError, enqueueSnackbar, isOpen, modifiersError]);

  useEffect(() => {
    if (!isOpen || areModifiersLoading) {
      return;
    }

    const account = getValues('account');
    if (
      account &&
      account !== NONE_OPTION_VALUE &&
      !accountOptions.some(option => option.value === account)
    ) {
      setValue('account', NONE_OPTION_VALUE);
    }

    const locationIds = getValues('locationIds');
    const validLocationIds = locationIds.filter(locationId =>
      findTreeNodeLabel(locationTree, locationId),
    );
    if (validLocationIds.length !== locationIds.length) {
      setValue('locationIds', validLocationIds);
    }

    setValue('optionalModifiers', {
      ...buildDefaultOptionalModifiers(modifierFieldsConfig),
      ...getValues('optionalModifiers'),
    });
  }, [
    accountOptions,
    areModifiersLoading,
    getValues,
    isOpen,
    locationTree,
    modifierFieldsConfig,
    setValue,
  ]);

  const handleDrawerClose = () => {
    reset(defaultValues);
    onClose();
  };

  useEffect(() => {
    if (!isCreateTeamDrawerRestore(lastRestoredItem?.id)) {
      return;
    }

    reset(
      deserializeCreateTeamFormData(
        lastRestoredItem?.data,
        modifierFieldsConfig,
      ),
    );
    onOpen();
    clearLastRestoredItem();
  }, [
    clearLastRestoredItem,
    lastRestoredItem,
    modifierFieldsConfig,
    onOpen,
    reset,
  ]);

  const onSubmit = (data: CreateTeamFormData) => {
    const payload = buildCreateTeamSubmitPayload(data, modifierFieldsConfig);

    createTeam(payload, {
      onSuccess: () => {
        handleDrawerClose();
        enqueueSnackbar('Team successfully created', { variant: 'success' });
      },
      onError: error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to create team'), {
          variant: 'error',
        });
      },
    });
  };

  const handleCreate = () => {
    clearErrors();
    handleSubmit(onSubmit)();
  };

  const handleHide = () => {
    minimizeDrawer(
      CREATE_TEAM_DRAWER_ID,
      'Create New Team',
      serializeCreateTeamFormData(getValues()),
    );
    handleDrawerClose();
  };

  return (
    <DiamDrawer
      title="Create New Team"
      hasHide
      isOpen={isOpen}
      handleDrawerClose={handleDrawerClose}
      confirmOnClose
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel adding Team"
      cancelButtonText="Dismiss"
      handleHide={handleHide}
    >
      <TeamFormFields
        control={control}
        isPending={isPending}
        applicationOptions={applicationOptions}
        areApplicationsLoading={areApplicationsLoading}
        parentTeamTree={parentTeamTree}
        isParentTeamLoading={isParentTeamLoading}
        accountOptions={accountOptions}
        locationTree={locationTree}
        areModifiersLoading={areModifiersLoading}
        selectedApplications={selectedApplications}
      />

      <FlexBox sx={{ justifyContent: 'flex-end', gridColumnGap: 24 }}>
        <DiamDrawerCancelButton disabled={isPending} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={isPending}
          startIcon={
            isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          Create
        </Button>
      </FlexBox>
    </DiamDrawer>
  );
}
